// In src/SupportTickets.js

import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, query, onSnapshot, orderBy, doc, updateDoc } from "firebase/firestore";
import { FaSpinner, FaPaperPlane, FaCheck, FaClock } from 'react-icons/fa';

// --- Ticket Detail Modal ---
const TicketDetailModal = ({ ticket, onClose, onReply, processing }) => {
    const [replyMessage, setReplyMessage] = useState('');
    const [newStatus, setNewStatus] = useState(ticket.status);

    const handleReply = () => {
        if (!replyMessage) return;
        onReply(ticket.id, replyMessage, newStatus);
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{maxWidth: '700px'}}>
                <div className="modal-header">
                    <h3>Ticket Details [#{ticket.id.substring(0, 6)}]</h3>
                    <button onClick={onClose} className="modal-close-btn">&times;</button>
                </div>
                <div className="modal-body">
                    {/* Ticket Info */}
                    <div className="detail-grid">
                        <div className="detail-item"><strong>Name:</strong> {ticket.name}</div>
                        <div className="detail-item"><strong>Email:</strong> {ticket.email}</div>
                        <div className="detail-item"><strong>User Type:</strong> {ticket.userType}</div>
                        <div className="detail-item"><strong>Reason:</strong> {ticket.reason}</div>
                        {ticket.driverEmail && <div className="detail-item"><strong>Driver Email:</strong> {ticket.driverEmail}</div>}
                    </div>
                    <hr />
                    {/* Conversation History */}
                    <div className="history-container">
                        {ticket.history?.map((item, index) => (
                            <div key={index} className={`history-item ${item.author === 'user' ? 'user-message' : 'admin-reply'}`}>
                                <p className="history-content">{item.content}</p>
                                <p className="history-meta">
                                    {item.author === 'user' ? ticket.name : item.author} - {item.timestamp?.toDate().toLocaleString()}
                                </p>
                            </div>
                        ))}
                    </div>
                    <hr />
                    {/* Reply Section */}
                    <div className="reply-section">
                        <h4>Reply to Ticket</h4>
                        <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            rows="5"
                            placeholder="Type your reply here..."
                            className="form-input"
                        />
                        <div className="reply-actions">
                            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="form-input">
                                <option value="in_progress">Mark as In Progress</option>
                                <option value="closed">Mark as Closed</option>
                            </select>
                            <button onClick={handleReply} disabled={processing} className="btn btn-primary">
                                {processing ? <FaSpinner className="animate-spin"/> : <FaPaperPlane />}
                                Send Reply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Main Component ---
export default function SupportTickets() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        const q = query(collection(db, "supportTickets"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setTickets(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleReply = async (ticketId, replyMessage, newStatus) => {
        setProcessing(true);
        try {
            const user = auth.currentUser;
            if (!user) throw new Error("Not logged in.");
            const idToken = await user.getIdToken(true);

            const response = await fetch("https://replytosupportticket-s7dx7q362a-uc.a.run.app", {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({ ticketId, replyMessage, newStatus })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to send reply.");
            }
            alert("Reply sent successfully!");
            setSelectedTicket(null);
        } catch (error) {
            alert(`Error: ${error.message}`);
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <p className="loading-text">Loading support tickets...</p>;

    return (
        <div className="card">
            <h2 className="page-header">Support Tickets</h2>
            <p className="page-subheader">View and respond to inquiries from users and drivers.</p>

            {selectedTicket && (
                <TicketDetailModal 
                    ticket={selectedTicket}
                    onClose={() => setSelectedTicket(null)}
                    onReply={handleReply}
                    processing={processing}
                />
            )}

            <table className="table">
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Name</th>
                        <th>Reason</th>
                        <th>Date</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {tickets.map(ticket => (
                        <tr key={ticket.id}>
                            <td><span className={`status-badge status-${ticket.status}`}>{ticket.status}</span></td>
                            <td>{ticket.name}</td>
                            <td>{ticket.reason}</td>
                            <td>{ticket.createdAt?.toDate().toLocaleString()}</td>
                            <td>
                                <button onClick={() => setSelectedTicket(ticket)} className="btn btn-view">
                                    View & Reply
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}